import { useState, useRef, useEffect } from "react";
import { useApi } from "../../hooks/use-api";
import {
  apiStudentStudys,
  API_SUBJECTS,
  API_AI_CONTENT_CHAPTERS,
  API_MCQ,
  apiChatHistoryFilter,
  API_MEDIUMS,
  API_AVATAR_ASK,
  API_AVATAR_START,
  API_AVATAR_STOP,
  API_AVATAR_LOG,
  API_CHAPTERS,
  API_STUDENT_ASSET_URL,
  API_VIDEO_CHAT,
  API_AUDIO_CHAT,
  API_VIDEO_MARK,
  API_LEARNING_CHALLENGE_UPDATE,
} from "../../utils/api-routes";
import { useSingleton } from "../../hooks/use-singleton";
import { useNotify } from "../../hooks/use-notify";

const useHandler = () => {
  const { get, post } = useApi();
  const { toggleNotify } = useNotify();
  const { mail, user, role, medium, board, section, examType } = useSingleton();
  const [inputText, setInputText] = useState("");
  const [conversation, setConversation] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEnable, setIsEnable] = useState(false);
  const [mode, setMode] = useState(false);
  const [avatarSession, setAvatarSession] = useState(null);
  const [lastAvatarSessionId, setLastAvatarSessionId] = useState(null);
  const [avatarToast, setAvatarToast] = useState(null);
  const [isHistoryEmpty, setIsHistoryEmpty] = useState(false);
  const [isLiveAvatarLoading, setIsLiveAvatarLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMedia, setPopupMedia] = useState(null);

  const [selectedMedium, setSelectedMedium] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedChapter, setSelectedChapter] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [question, setQuestion] = useState("");

  const mediumRef = useRef(null);
  const subjectRef = useRef(null);
  const chapterRef = useRef(null);
  const languageRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const audioRef = useRef(null);
  const videoRef = useRef(null);

  const [classdetails, setClassDetails] = useState({});
  const [mediums, setMediums] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [assetData, setAssetData] = useState([]);
  const [showAskBox, setShowAskBox] = useState(true);
  const [audioRes, setAudioRes] = useState({});
  const [videoRes, setVideoRes] = useState({});

  const isStartEnabled =
    !!selectedSubject && selectedChapter.length > 0 && !!selectedLanguage;

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async () => {
    if (!inputText.trim()) return;

    if (
      !classdetails?.data?.class ||
      !selectedSubject ||
      selectedChapter.length === 0 ||
      !selectedLanguage ||
      !medium
    ) {
      alert(
        "Please select all required fields: Class, Subject, Chapter, Language",
      );
      return;
    }

    const question = inputText.trim();

    // Add user message
    const userMessage = { sender: "user", text: question };
    setConversation((prev) => [...prev, userMessage]);

    // Clear input box
    setInputText("");

    setIsLoading(true);

    // 2️⃣ Add temporary loading bot message
    setConversation((prev) => [
      ...prev,
      { sender: "bot", text: "Loading...", isLoading: true },
    ]);

    try {
      const payload = {
        mail: mail,
        board: classdetails?.data?.board,
        clas: classdetails?.data?.class,
        subject: selectedSubject,
        chapter: selectedChapter.join(", "),
        question: question, // Fixed bug where stale inputText was used after being cleared
        language: selectedLanguage,
        medium: medium,
        section,
      };
      const avaterPayload = {
        mail: mail,
        board: classdetails?.data?.board,
        clas: classdetails?.data?.class,
        subject: selectedSubject,
        chapter: selectedChapter.join(", "),
        question: question, // Fixed bug where stale inputText was used after being cleared
        language: selectedLanguage,
        medium: medium,
        section,
      };

      if (mode) {
        // --- LIVE AVATAR INTERACTION ---
        const response = await post(API_AVATAR_ASK, avaterPayload, {
          headers: {
            "x-user-mail": mail || user?.data?.email || "",
          },
        });

        if (response.status !== 200 && response.status !== 201) {
          throw new Error(`Avatar Ask failed: ${response.status}`);
        }

        // Remove the temporary loading bubble, since the Avatar will physically speak the reply
        setConversation((prev) => prev.filter((msg) => !msg.isLoading));
      } else {
        // --- STANDARD TEXT CHATBOT ---
        const response = await post(API_MCQ, payload);

        setConversation((prev) =>
          prev.map((msg) =>
            msg.isLoading
              ? {
                  sender: "bot",
                  text:
                    response?.data?.answer ||
                    "Sorry, I couldn't understand that.",
                  audioPath: response?.data?.audio_path || null,
                }
              : msg,
          ),
        );
      }
    } catch (error) {
      console.error("API Error:", error);
      setConversation((prev) =>
        prev.map((msg) =>
          msg.isLoading
            ? {
                sender: "bot",
                text: "Sorry, there was an error processing your request.",
              }
            : msg,
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        mediumRef.current?.contains(e.target) ||
        subjectRef.current?.contains(e.target) ||
        chapterRef.current?.contains(e.target) ||
        languageRef.current?.contains(e.target)
      )
        return;

      setOpenDropdown(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getUserStudiesDetails = async () => {
    try {
      const res = await get(apiStudentStudys(mail));
      if (res) {
        // console.log(res?.data)
        setClassDetails(res?.data);
        const payload = {
          board: res?.data?.data?.board,
          clas: res?.data?.data?.class,
        };
        getMediumDetails(payload);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      console.log("Error on getting user studiesdetails", e);
    }
  };

  const getMediumDetails = async (payload) => {
    setIsLoading(true);
    try {
      const res = await post(API_MEDIUMS, payload);
      if (res) {
        setMediums(res?.data?.mediums);
        setIsLoading(false);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting Subject Details", e);
    }
  };

  const getSubjectDetails = async (payload) => {
    setIsLoading(true);
    try {
      const res = await post(API_SUBJECTS, payload);
      if (res) {
        setSubjects(res?.data?.subjects);
        setIsLoading(false);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting Subject Details", e);
    }
  };

  const getChapterDetails = async (payload) => {
    setIsLoading(true);
    const url = mode ? API_AI_CONTENT_CHAPTERS : API_CHAPTERS;
    try {
      const res = await post(url, payload);
      if (res) {
        setChapters(res?.data?.chapters);
        setIsLoading(false);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting chapeter details", e);
    }
  };

  useEffect(() => {
    getUserStudiesDetails();
  }, []);

  useEffect(() => {
    if (!medium || !classdetails?.data?.class) return;
    const payload = {
      board: classdetails?.data?.board,
      clas: classdetails?.data?.class,
      medium: medium,
    };

    getSubjectDetails(payload);
  }, [medium, user, classdetails?.data?.class]);

  useEffect(() => {
    if (!selectedSubject || !classdetails?.data?.class) return;
    // Clear previously selected chapters
    setSelectedChapter([]);
    const payload = {
      //  board:"NCERT",
      board: classdetails?.data?.board,
      clas: classdetails?.data?.class,
      subject: selectedSubject,
      medium: medium,
    };
    getChapterDetails(payload);
  }, [selectedSubject]);

  // useEffect(()=>{
  //     setConversation([]);
  //     setIsEnable(false);
  // },[selectedSubject, selectedChapter, selectedLanguage])

  useEffect(() => {
    if (!isEnable) {
      setConversation([]);
    }
  }, [selectedSubject, selectedChapter, selectedLanguage]);

  const onClickStart = () => {
    setConversation([]);
    setIsEnable(true);
    setIsHistoryEmpty(false);
  };

  const getHistoryDetails = async () => {
    try {
      const res = await get(
        apiChatHistoryFilter(
          mail,
          selectedSubject,
          selectedChapter.join(", "),
          section,
        ),
      );
      if (res?.data?.history.length) {
        const formattedConversation = [];

        res?.data?.history?.forEach((item) => {
          // const payload = item?.payload;

          // user message
          formattedConversation.push({
            sender: "user",
            text: item.user_question,
            time: item.created_at,
          });

          // bot message
          formattedConversation.push({
            sender: "bot",
            text: item?.ai_answer,
            audioPath: item?.audio_path || null,
          });
        });

        setConversation(formattedConversation);
        setIsHistoryEmpty(false);
      } else {
        setConversation([]);
        setIsHistoryEmpty(true);
      }
    } catch (e) {
      console.log("ERROR IS COMES FROM GETHISTORY API", e);
    }
  };

  const onClickHistroy = async () => {
    if (isStartEnabled) {
      await getHistoryDetails();
      setIsEnable(true);
    }
  };

  const onToggleBtn = () => {
    if (role !== "guest") {
      setMode((p) => !p);
      setConversation([]);
      setIsEnable(false);
      setAvatarSession(null);
    } else {
      toggleNotify({
        type: "success",
        title: "Message",
        description: "Please Subscribe",
      });
    }
  };

  const onLiveAvatarApi = async () => {
    setIsEnable(true);
    setIsLiveAvatarLoading(true);

    setConversation([
      { sender: "bot", text: "Connecting to Live Avatar...", isLoading: true },
    ]);

    try {
      const response = await post(
        `${API_AVATAR_START}?language=${selectedLanguage}`,
        {},
        {
          headers: {
            "x-user-mail": mail || user?.data?.email || "",
          },
        },
      );

      console.log("AVATAR API STATUS:", response.status);
      const data = response.data;
      console.log("AVATAR START RESPONSE:", data);

      if (response.status === 200 || response.status === 201) {
        setAvatarSession(data || { connected: true });
        if (data?.session_id) setLastAvatarSessionId(data.session_id);
        setIsLiveAvatarLoading(false);
        setConversation([
          {
            sender: "bot",
            text: "Live Avatar connected successfully! You can now start interacting.",
          },
        ]);
      } else {
        throw new Error(data?.message || `Error: ${response.status}`);
      }
    } catch (err) {
      console.error("Error starting Avatar API:", err);
      setIsEnable(false);
      const msg = err?.message || "Unable to connect to Live Avatar.";
      setAvatarToast({
        type: "error",
        message:
          msg.includes("502") || msg.includes("403")
            ? "Unable to connect: Avatar service unavailable."
            : msg,
      });
      setTimeout(() => setAvatarToast(null), 4000);
      setConversation([]);
    } finally {
      setIsLiveAvatarLoading(false);
    }
  };

  const onEndAvatarInteraction = async () => {
    setConversation([]);
    setIsEnable(false);
    setAvatarSession(null);
    try {
      const response = await post(
        API_AVATAR_STOP,
        {},
        {
          headers: {
            "x-user-mail": mail || user?.data?.email || "",
          },
        },
      );
      console.log("AVATAR STOP RESPONSE STATUS:", response.status);
    } catch (err) {
      console.error("Error stopping Avatar API:", err);
    }
  };

  const downloadAvatarLog = async () => {
    const sid = avatarSession?.session_id || lastAvatarSessionId;
    if (!sid) {
      alert("No active avatar session found to download logs.");
      return;
    }
    try {
      const response = await get(`${API_AVATAR_LOG}?session_id=${sid}`, {
        headers: {
          "x-user-mail": mail || user?.data?.email || "",
        },
      });
      const data = response?.data;
      if (response && response.status === 200) {
        if (response.data?.pdf_url) {
          window.open(response.data.pdf_url, "_blank");
        } else {
          alert("Unable to generate log PDF.");
        }
      }
    } catch (err) {
      console.error("Error downloading log:", err);
      alert("Error downloading log file.");
    }
  };

  const handleClearChat = () => {
    setConversation([]); // clear messages only
  };

  const handleNewChat = () => {
    // clear chat
    setConversation([]);

    // reset filters
    setSelectedSubject(null);
    setSelectedChapter([]);
    setSelectedLanguage(null);

    // reset input
    setInputText("");
    setOpenDropdown(null);

    // ✅ IMPORTANT
    setIsEnable(false); // 👈 this will hide input section

    // optional resets
    setIsHistoryEmpty(false);
    onEndAvatarInteraction?.();
  };

  const onClickLearnVideo = async () => {
    setConversation([]);
    setIsHistoryEmpty(false);
    setShowPopup(true);

    try {
      const payload = {
        mail: mail,
        board: classdetails?.data?.board,
        clas: classdetails?.data?.class,
        medium: medium,
        chapter: selectedChapter.join(", "),
        subject: selectedSubject,
        language: selectedLanguage,
        section,
      };

      const res = await post(API_STUDENT_ASSET_URL, payload);

      if (res) {
        setAssetData(res?.data?.assets || []);
      }
    } catch (error) {
      console.error("API error:", error);
    }
  };

  const aiAudioChat = async (filter, audio_content) => {
    const payload = {
      Audio_content: audio_content,
      // filters: audio_content,
      Question: question,
      language: "English",
    };

    try {
      const res = await post(API_AUDIO_CHAT, payload);
      if (res) {
        setAudioRes(res?.data);
      }
    } catch (e) {
      console.log("ERROR COMES FROM AUDIO CHAT API", e);
    }
  };

  const aiVideoChat = async (video_content) => {
    const payload = {
      // Video_content: video_content,
      filters: {
        board: classdetails?.data?.board,
        clas: classdetails?.data?.class,
        medium: medium,
        subject: selectedSubject,
        chapter: selectedChapter.join(","),
      },
      Question: question,
      language: "English",
    };

    try {
      const res = await post(API_VIDEO_CHAT, payload);
      if (res) {
        setVideoRes(res?.data);
      }
    } catch (e) {
      console.log("ERROR COMES FROM AI VIDEO CHAT API", e);
    }
  };

  const learningChallengeProgress = async () => {
    const payload = {
      email: mail,
      action_type: "video",
      watch_percentage: 100,
      questions_attempted: 0,
      study_minutes: 0,
    };
    try {
      const res = await post(API_LEARNING_CHALLENGE_UPDATE, payload);
      if (res) {
        console.log(res);
      }
    } catch (e) {
      console.log("ERROR COMES FROM LEARNING CHALLANGE API", e);
    }
  };

  const videoMarkCompleted = async (videoUrl) => {
    const payload = {
      user_email: mail,
      video_url: videoUrl,
      course_type: examType,
      filters: {
        clas: classdetails?.data?.class,
        subject: selectedSubject,
        chapter: selectedChapter.join(", "),
      },
    };
    try {
      const res = await post(API_VIDEO_MARK, payload);
      if (res.data) {
        await learningChallengeProgress();
      }
    } catch (e) {
      console.log("ERROR COMES FROM VIDEO MARK COMPELETED", e);
    }
  };

  return {
    handleClearChat,
    handleNewChat,
    subjectRef,
    chapterRef,
    languageRef,
    conversation,
    selectedChapter,
    selectedLanguage,
    selectedSubject,
    openDropdown,
    inputText,
    classdetails,
    subjects,
    chapters,
    isLoading,
    user,
    isStartEnabled,
    isEnable,
    mode,
    avatarSession,
    avatarToast,
    selectedMedium,
    mediums,
    mediumRef,
    isHistoryEmpty,
    showPopup,
    assetData,
    scrollRef,
    popupMedia,
    audioRef,
    videoRef,
    inputRef,
    showAskBox,
    audioRes,
    videoRes,
    question,
    role,
    setQuestion,
    setShowAskBox,
    aiAudioChat,
    aiVideoChat,
    showAskBox,
    setShowAskBox,
    setPopupMedia,
    setShowPopup,
    onToggleBtn,
    onClickHistroy,
    onClickStart,
    setSelectedSubject,
    setSelectedChapter,
    setSelectedLanguage,
    setOpenDropdown,
    setInputText,
    handleSend,
    handleKeyPress,
    getHistoryDetails,
    onLiveAvatarApi,
    onEndAvatarInteraction,
    downloadAvatarLog,
    onClickLearnVideo,
    videoMarkCompleted,
    lastAvatarSessionId,
    setSelectedMedium,
    isLiveAvatarLoading,
  };
};

export default useHandler;
